// @file Vuex Store module for Activity panel
import { getPostPublishedAt } from '@@/bits/post_timestamps'
import { JsonAPIResource, JsonAPIResponse } from '@padlet/arvo'
import { addDays, sameDay } from 'bits/date'
import { captureFetchException } from 'bits/error_tracker'
import { __ } from 'bits/intl'
import PromiseQueue from 'bits/promise_queue'
import { WallFollows as WallFollowsApi } from 'dashboard/padlet_api'
import { orderBy } from 'lodash-es'
import { Cid, Id, Post, PostFeedItemData, UserId, WallFollowApiResponse, WallId } from 'types'
import { Module } from 'vuex'
import { SnackbarNotificationType } from 'vuexstore/modules/global_snackbar'
import { RootState } from 'vuexstore/surface/types'

enum FeedItemType {
  PostCreated = 'PostCreated',
}

interface FeedItem {
  id: Cid | Id
  type: FeedItemType
  data: PostFeedItemData
  emittedAt: string
}

enum FeedItemGroup {
  Today = 'Today',
  Yesterday = 'Yesterday',
  Earlier = 'Earlier',
}

enum FeedItemsStatus {
  Loading = 'Loading',
  Completed = 'Completed',
  Errored = 'Errored',
}

enum WallFollowCreationStatus {
  Initial = 'Initial',
  Loading = 'Loading',
  Completed = 'Completed',
}

interface ActivityPanelState {
  xActivityPanel: boolean
  wallFollowCreationStatus: WallFollowCreationStatus
  feedItemsStatus: FeedItemsStatus
  earlierFeedItemsPage: number
}

// Promise queue to handle update requests.
const q = new PromiseQueue()

const EARLIER_FEED_ITEMS_LIMIT = 30

const ActivityPanelModule: Module<ActivityPanelState, RootState> = {
  namespaced: true,
  state: {
    xActivityPanel: false,
    wallFollowCreationStatus: WallFollowCreationStatus.Initial,
    feedItemsStatus: FeedItemsStatus.Loading,
    earlierFeedItemsPage: 1,
  },
  getters: {
    xActivityPanel: (state): boolean => state.xActivityPanel,
    isCreatingWallFollow: (state): boolean => state.wallFollowCreationStatus === WallFollowCreationStatus.Loading,
    isLoadingFeedItems: (state): boolean => state.feedItemsStatus === FeedItemsStatus.Loading,
    earlierFeedItemsPage: (state): number => state.earlierFeedItemsPage,
    earlierFeedItemsCurrentLimit: (_, getters): number => getters.earlierFeedItemsPage * EARLIER_FEED_ITEMS_LIMIT,
    isLastEarlierFeedItemsPage: (_, getters): boolean =>
      getters.earlierFeedItemsCurrentLimit >= getters.sortedEarlierFeedItems.length,
    // 1. Map each type of feed item.
    postFeedItems: (_, getters, rootState, rootGetters): PostFeedItemData[] => {
      if (getters.isLoadingFeedItems) return []
      return rootGetters['post/publishedPosts'].map((post: Post): PostFeedItemData => {
        return {
          post,
          author: rootGetters['userContributor/getUserById'](post.author_id),
        }
      })
    },
    // 2. Merge all groups of feed items.
    feedItems: (_, getters): FeedItem[] => {
      // Right now, we're only showing one type of feed item.
      // We can update this getter once we add new types!
      return getters.postFeedItems.map((postFeedItem: PostFeedItemData): FeedItem => {
        return {
          id: postFeedItem.post.cid,
          type: FeedItemType.PostCreated,
          data: postFeedItem,
          emittedAt: String(getPostPublishedAt(postFeedItem.post)),
        }
      })
    },
    // 3. Group feed items.
    groupedFeedItems: (_, getters): Record<FeedItemGroup, FeedItem[]> => {
      const todayFeedItems: FeedItem[] = []
      const yesterdayFeedItems: FeedItem[] = []
      const earlierFeedItems: FeedItem[] = []
      const today = new Date()
      const yesterday = addDays(today, -1)
      for (const feedItem of getters.feedItems) {
        const emittedAt = new Date(feedItem.emittedAt)
        if (sameDay(emittedAt, today)) {
          todayFeedItems.push(feedItem)
        } else if (sameDay(emittedAt, yesterday)) {
          yesterdayFeedItems.push(feedItem)
        } else {
          earlierFeedItems.push(feedItem)
        }
      }
      return {
        [FeedItemGroup.Today]: todayFeedItems,
        [FeedItemGroup.Yesterday]: yesterdayFeedItems,
        [FeedItemGroup.Earlier]: earlierFeedItems,
      }
    },
    // 4. Sort each feed item group.
    getSortedFeedItems:
      (_, getters) =>
      (feedItemGroup: FeedItemGroup): FeedItem[] => {
        return orderBy(getters.groupedFeedItems[feedItemGroup], 'emittedAt', 'desc')
      },
    sortedTodayFeedItems: (_, getters): FeedItem[] => {
      return getters.getSortedFeedItems(FeedItemGroup.Today)
    },
    sortedYesterdayFeedItems: (_, getters): FeedItem[] => {
      return getters.getSortedFeedItems(FeedItemGroup.Yesterday)
    },
    sortedEarlierFeedItems: (_, getters): FeedItem[] => {
      return getters.getSortedFeedItems(FeedItemGroup.Earlier)
    },
    limitedSortedEarlierFeedItems: (_, getters): FeedItem[] => {
      return getters.sortedEarlierFeedItems.slice(0, getters.earlierFeedItemsCurrentLimit)
    },
  },
  mutations: {
    TOGGLE_ACTIVITY_PANEL(state, xActivityPanel): void {
      state.xActivityPanel = xActivityPanel
    },
    SET_WALL_FOLLOW_CREATION_STATUS(state, wallFollowCreationStatus: WallFollowCreationStatus): void {
      state.wallFollowCreationStatus = wallFollowCreationStatus
    },
    SET_FEED_ITEMS_STATUS(state, feedItemsStatus: FeedItemsStatus): void {
      state.feedItemsStatus = feedItemsStatus
    },
    SET_EARLIER_FEED_ITEMS_PAGE(state, earlierFeedItemsPage: number): void {
      state.earlierFeedItemsPage = earlierFeedItemsPage
    },
  },
  actions: {
    showActivityPanel({ commit, dispatch }): void {
      void dispatch('fetchAuthors')
      commit('TOGGLE_ACTIVITY_PANEL', true)
    },
    closeActivityPanel({ commit }): void {
      commit('TOGGLE_ACTIVITY_PANEL', false)
    },
    async fetchAuthors({ commit, dispatch }): Promise<void> {
      commit('SET_FEED_ITEMS_STATUS', FeedItemsStatus.Loading)
      try {
        await dispatch('userContributor/fetchAndWaitForAuthors', null, { root: true })
        commit('SET_FEED_ITEMS_STATUS', FeedItemsStatus.Completed)
      } catch (e) {
        commit('SET_FEED_ITEMS_STATUS', FeedItemsStatus.Errored)
      }
    },
    toggleWallFollow({ dispatch }, { wallFollowId, follows }: { wallFollowId: Id; follows: boolean }): void {
      void dispatch('updateWallFollows', { wallFollowId, followsWall: follows }, { root: true })
      q.enqueue('updateWallFollow', () =>
        dispatch('updateWallFollow', {
          wallFollowId,
          follows,
        }),
      )
    },
    async updateWallFollow(
      { dispatch },
      { wallFollowId, follows }: { wallFollowId: Id; follows: boolean },
    ): Promise<void> {
      try {
        await WallFollowsApi.update(
          { wallFollowId },
          {
            body: JSON.stringify({
              data: {
                attributes: { follows },
              },
            }),
          },
        )
      } catch (e) {
        // If one of the requests fails, we don't want to execute
        // the following requests.
        q.clear()
        captureFetchException(e, { source: 'WallFollowUpdate' })
        void dispatch('showGenericError')
        void dispatch('updateWallFollows', { wallFollowId, followsWall: !follows }, { root: true })
      }
    },
    async createWallFollow(
      { commit, dispatch },
      { wallId, userId, follows }: { wallId: WallId; userId: UserId; follows: boolean },
    ): Promise<void> {
      commit('SET_WALL_FOLLOW_CREATION_STATUS', WallFollowCreationStatus.Loading)
      try {
        const response: JsonAPIResponse<WallFollowApiResponse> = await WallFollowsApi.create({
          body: JSON.stringify({
            data: {
              attributes: { wall_id: wallId, user_id: userId, follows },
            },
          }),
        })
        void dispatch(
          'updateWallFollows',
          { wallFollowId: Number((response.data as JsonAPIResource<WallFollowApiResponse>).id), followsWall: follows },
          { root: true },
        )
      } catch (e) {
        captureFetchException(e, { source: 'WallFollowCreation' })
        void dispatch('showGenericError')
      } finally {
        commit('SET_WALL_FOLLOW_CREATION_STATUS', WallFollowCreationStatus.Completed)
      }
    },
    showGenericError({ dispatch }): void {
      void dispatch(
        'globalSnackbar/setSnackbar',
        {
          message: __('Something went wrong. Please try again later.'),
          notificationType: SnackbarNotificationType.error,
          timeout: 1500,
        },
        { root: true },
      )
    },
    loadMoreFeedItems({ commit, getters }): void {
      commit('SET_EARLIER_FEED_ITEMS_PAGE', getters.earlierFeedItemsPage + 1)
    },
  },
}

export default ActivityPanelModule
export { ActivityPanelState, FeedItem, FeedItemType }
